/**
 * RENDERABLE certificate template (printable HTML → system PDF).
 *
 * Content comes from server-emitted snapshot normalized by
 * `buildCertificateContent` (@aula/shared): student name, course, sealed
 * version, issuing academy, date, and public verification id/URL. Theme-aware
 * (surface/accent tokens) with legible branding in any theme (solid
 * chip bg-accent + text-accent-foreground).
 */
import { msg } from "@lingui/core/macro";
import { Award } from "@fleet/icons";
import { type Component, Show } from "solid-js";
import type { CertificateContent } from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";

export const CertificateCard: Component<{ content: CertificateContent }> = (props) => {
  const { t } = useI18n();
  return (
    <div
      data-certificate-print
      data-testid="certificate-card"
      class="relative overflow-hidden rounded-3xl border-2 border-accent/40 bg-surface p-8 text-center shadow-lg md:p-12"
    >
      {/* Marco decorativo interior */}
      <div class="pointer-events-none absolute inset-3 rounded-2xl border border-accent/25" />

      <div class="relative flex flex-col items-center gap-4">
        {/* Marca legible en cualquier tema (chip sólido, patrón logo theme-safe) */}
        <span class="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 font-display text-sm font-extrabold text-accent-foreground">
          <Award class="h-4 w-4" />
          Aula
        </span>

        <p class="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">
          {t(msg`Certificate of completion`)}
        </p>

        <p class="text-sm text-muted-foreground">{t(msg`This certifies that`)}</p>
        <h1
          class="font-display text-3xl font-extrabold tracking-tight md:text-4xl"
          data-testid="certificate-learner"
        >
          {props.content.learnerName}
        </h1>

        <p class="text-sm text-muted-foreground">{t(msg`has successfully completed the course`)}</p>
        <h2
          class="font-display text-xl font-bold text-accent md:text-2xl"
          data-testid="certificate-course"
        >
          {props.content.courseTitle}
        </h2>

        <Show when={props.content.orgName}>
          {(org) => (
            <p class="text-sm font-medium" data-testid="certificate-org">
              {t(msg`Issued by {org}`, { org: org() })}
            </p>
          )}
        </Show>

        <div class="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span data-testid="certificate-date">{props.content.issuedAtLabel}</span>
          <span data-testid="certificate-version">
            {t(msg`Course content {version}`, { version: props.content.versionLabel })}
          </span>
        </div>

        {/* Credencial de verificación pública */}
        <div class="mt-4 w-full rounded-2xl bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
          <p class="font-semibold uppercase tracking-wide">{t(msg`Verify this certificate`)}</p>
          <p class="mt-1 break-all font-mono" data-testid="certificate-verify-url">
            {props.content.verifyUrl}
          </p>
          <p class="mt-1 break-all font-mono opacity-80" data-testid="certificate-id">
            {t(msg`ID: {certId}`, { certId: props.content.certId })}
          </p>
        </div>
      </div>
    </div>
  );
};
